<script lang="ts">
  import { getContext } from "svelte"
  import { writable } from "svelte/store"
  import type { Writable } from "svelte/store"
  import type { Audience } from "$lib/types/landing"

  // Import our matching components
  import StudentROICalculator from "$lib/components/landing/calculators/StudentROICalculator.svelte"
  import UniversityROICalculator from "$lib/components/landing/calculators/UniversityROICalculator.svelte"

  // Icons from prototype (exact matches)
  import {
    Briefcase,
    Brain,
    ChartBar,
    DollarSign,
    UserPlus,
    Headphones,
    Search,
    Target,
    Users,
    Award,
    CheckCircle,
    XCircle,
    MinusCircle,
    Shield,
    Activity,
    FileText,
  } from "lucide-svelte"

  // Get audience context from layout with proper fallback
  let audience: Writable<Audience>
  try {
    audience =
      getContext<Writable<Audience>>("audience") ||
      writable<Audience>("students")
  } catch {
    audience = writable<Audience>("students")
  }

  // Student vs University content (exact from prototypes)
  const heroContent = {
    students: {
      title: "Land Your Six-Figure Finance Job with Confidence",
      subtitle:
        "Your AI-powered coach for mastering interviews at top investment banks, private equity firms, hedge funds, and venture capital.",
      primaryButton: "Start Free Trial",
      secondaryButton: "Watch Demo",
      trustText: "Trusted by students from leading institutions",
    },
    universities: {
      title: "Elevate Your University's Finance Career Outcomes",
      subtitle:
        "Partner with AceTheRound to provide your students with cutting-edge AI-powered interview preparation that delivers measurable results.",
      primaryButton: "Request Partnership Info",
      secondaryButton: "Watch Demo",
      trustText: "Trusted by career services at leading institutions",
    },
  }

  // Finance sectors data (exact from student prototype)
  const financeSectors = [
    {
      title: "Investment Banking",
      description:
        "Master technical questions and case studies for top-tier investment banks.",
      icon: Briefcase,
      logos: [
        "/images/landing_page/investment_banking/goldman-sachs-logo.png",
        "/images/landing_page/investment_banking/jp-morgan-logo.png",
        "/images/landing_page/investment_banking/morgan-stanley-logo.png",
      ],
    },
    {
      title: "Private Equity",
      description:
        "Prepare for rigorous LBO modeling tests and industry-specific questions.",
      icon: DollarSign,
      logos: [
        "/images/landing_page/private_equity/blackstone-logo.png",
        "/images/landing_page/private_equity/kkr-logo.png",
        "/images/landing_page/private_equity/carlyle-logo.png",
      ],
    },
    {
      title: "Hedge Funds",
      description: "Practice investment pitches and quantitative assessments.",
      icon: ChartBar,
      logos: [
        "/images/landing_page/hedge_funds/citadel-logo.png",
        "/images/landing_page/hedge_funds/bridgewater-associates-logo.png",
        "/images/landing_page/hedge_funds/renaissance-logo.png",
      ],
    },
    {
      title: "Venture Capital",
      description:
        "Learn to analyze startups and articulate investment theses.",
      icon: Target,
      logos: [
        "/images/landing_page/venture_capital/a16z.png",
        "/images/landing_page/venture_capital/sequoia.png",
        "/images/landing_page/venture_capital/khosla.png",
      ],
    },
  ]

  // Student USPs (exact from prototype)
  const studentUSPs = [
    {
      icon: Brain,
      title: "AI-Powered Adaptive Learning",
      description:
        "Pinpoint weaknesses, get targeted drills that adapt to your progress.",
      stat: "75% mastered weak areas within 8 sessions",
    },
    {
      icon: ChartBar,
      title: "Instant, Data-Driven Feedback",
      description: "Receive immediate insights to accelerate your improvement.",
      stat: "30% faster improvement on technical questions",
    },
    {
      icon: Briefcase,
      title: "Exact Skills Top Firms Demand",
      description:
        "Practice with real interview Q&A curated from top-tier firms.",
      stat: "15,000+ real-world questions",
    },
    {
      icon: DollarSign,
      title: "Affordable, High ROI",
      description: "Less than a latte a day for a six-figure salary.",
      stat: "2,000 first-time finance candidates landed $100k+ offers",
    },
    {
      icon: UserPlus,
      title: "Exclusive Community & Coaching",
      description: "Join weekly calls & peer network for extra support.",
      stat: "Members who attend calls see a 40% higher offer rate",
    },
    {
      icon: Headphones,
      title: "Realistic Voice Simulation",
      description: "Practice with AI-powered voice interviews that feel real.",
      stat: "90% of users report increased confidence in live interviews",
    },
  ]

  // How it works steps (exact from prototype)
  const howItWorksSteps = [
    {
      title: "Identify Skill Gaps",
      description:
        "Take our comprehensive assessment to pinpoint your strengths and weaknesses.",
      icon: Search,
    },
    {
      title: "Targeted Drills",
      description:
        "Engage in AI-powered exercises designed to rapidly improve your weak areas.",
      icon: Target,
    },
    {
      title: "Mock Interviews",
      description:
        "Practice with realistic, AI-driven interviews tailored to your target roles.",
      icon: Users,
    },
    {
      title: "Final Simulation",
      description:
        "Face a full-length, high-stakes interview simulation to ensure you're fully prepared.",
      icon: Award,
    },
  ]

  // Student testimonials (exact from prototype)
  const studentTestimonials = [
    {
      name: "Alex K., Harvard Business School",
      position: "Secured $180K offer at Goldman Sachs",
      content:
        "AceTheRound was a game-changer. The AI feedback helped me nail those tricky technical questions. I walked into Goldman with the confidence of a seasoned pro!",
    },
    {
      name: "Priya S., UC Berkeley",
      position: "Landed $200K position at Blackstone",
      content:
        "I was terrified of case interviews, but AceTheRound's practice scenarios made them a breeze. The personalized tips took my responses from good to exceptional.",
    },
  ]

  // University testimonials (exact from prototype)
  const universityTestimonials = [
    {
      name: "Dr. Sarah Chen, Career Services Director",
      position: "Top 10 Business School",
      content:
        "Implementing AceTheRound has been transformative for our finance program. We've seen a 15% increase in placement rates at top firms, and our students report feeling significantly more confident in interviews. The analytics dashboard has also helped us identify curriculum gaps we needed to address.",
    },
    {
      name: "Professor James Wilson, Finance Department Chair",
      position: "Leading Public University",
      content:
        "As a public university competing with elite private institutions, AceTheRound has been a game-changer. Our students now have access to the same quality of interview preparation as their peers at top schools, and employers have noticed the difference. The ROI has been exceptional.",
    },
  ]

  // Pricing from prototype (exact copy)
  const pricingPlans = [
    {
      name: "Basic",
      description: "For the ambitious starter",
      price: "$69",
      period: "/month",
      dailyCost: "Just $2.30/day",
      features: [
        "10 AI-powered interviews/month",
        "AI-Powered Adaptive Learning",
        "Instant, Data-Driven Feedback",
        "Exclusive Community Access",
        "1 monthly group coaching call",
      ],
      buttonText: "Start Free Trial",
      highlight: false,
    },
    {
      name: "Premium",
      description: "For the determined achiever",
      price: "$99",
      period: "/month",
      dailyCost: "Just $3.30/day",
      features: [
        "20 AI-powered interviews/month",
        "All Basic features plus:",
        "Advanced Adaptive Learning",
        "Weekly group coaching calls",
        "Priority support",
      ],
      buttonText: "Start Free Trial",
      highlight: true,
    },
  ]

  // FAQ from prototype (exact copy)
  const faqs = [
    {
      question: "Can this really help me land a $100,000+ finance job?",
      answer:
        "Absolutely. Our system has already helped countless candidates secure six-figure roles at top firms. With our advanced AI-powered prep, you'll gain a significant edge over your competition. The ROI speaks for itself – a small investment now can lead to a substantial salary increase.",
    },
    {
      question: "I don't have a finance background—can I still succeed?",
      answer:
        "Yes, many of our most successful users started without prior finance experience. Our adaptive learning system builds your skills from the ground up, ensuring you master even the most complex concepts. If others have transitioned into finance using our platform, so can you.",
    },
    {
      question: "What happens after I land the job?",
      answer:
        "That's when AceTheRound becomes even more valuable. Beyond interview prep, our platform continually enhances your communication, leadership, and client-facing skills—critical for excelling and advancing in high-stakes finance roles. It's your competitive edge for long-term career success.",
    },
    {
      question:
        "Are the interview questions tailored to firms like Goldman Sachs or Blackstone?",
      answer:
        "Absolutely. Our question bank is meticulously curated with input from successful candidates and industry insiders. We focus on what top firms in Investment Banking, Private Equity, Hedge Funds, Equity Research, and Venture Capital actually ask, ensuring you're prepared for the specific challenges of your target roles.",
    },
    {
      question: "How quickly can I feel confident for my interviews?",
      answer:
        "Many users report significant breakthroughs after just a few sessions. With our AI-powered instant feedback and targeted drills, you'll sharpen your skills rapidly. Most users feel fully prepared in as little as two weeks of consistent practice, giving them a marked advantage in their interviews.",
    },
    {
      question: "Why is AceTheRound better than other prep tools?",
      answer:
        "AceTheRound is built specifically for finance careers, using AI to adapt to your unique strengths and weaknesses. Unlike generic guides or expensive coaching, we offer 24/7 access to personalized, dynamic preparation at a fraction of the cost. With us, there's no guesswork—just targeted, efficient improvement that directly translates to interview success.",
    },
  ]

  // Comparison table data from prototype
  const comparisonFeatures = [
    "AI-Powered Adaptive Learning",
    "Role-Specific Prep (IB, PE, HF, ER, VC)",
    "Realistic Voice Simulation",
    "Instant, Data-Driven Feedback",
    "24/7 Flexible Practice",
    "Continuous Career Development",
    "Cost-Effective Investment",
    "Success Bonus & Community",
  ]

  const comparisonData = [
    [true, true, true, true, true, true, true, true], // AceTheRound
    [false, "partial", false, false, "partial", false, "partial", false], // Generic Guides
    [false, false, false, false, true, "partial", "partial", "partial"], // Online Courses
    [false, "partial", true, true, false, true, false, "partial"], // 1-on-1 Coaching
  ]

  const comparisonColumns = [
    "AceTheRound",
    "Generic Guides",
    "Online Courses",
    "1-on-1 Coaching",
  ]
</script>

<div class="min-h-screen bg-white">
  <!-- Hero Section (using our component) -->
  <section class="bg-white py-20">
    <div class="container mx-auto px-6 flex flex-col md:flex-row items-center">
      <div class="md:w-1/2 mb-8 md:mb-0 pr-0 md:pr-12">
        <h1
          class="text-4xl md:text-5xl font-bold mb-6 leading-tight text-accent-content"
        >
          {heroContent[$audience].title}
        </h1>
        <p class="text-xl mb-8 text-accent-content leading-relaxed">
          {heroContent[$audience].subtitle}
        </p>
        <div class="flex flex-col sm:flex-row gap-4">
          <a
            href={$audience === "students" ? "/pricing" : "/contact_us"}
            class="btn btn-lg bg-accent text-accent-content hover:bg-yellow-500 font-medium px-8"
          >
            {heroContent[$audience].primaryButton}
          </a>
          <button
            class="btn btn-lg btn-outline border-primary text-primary hover:bg-primary hover:text-primary-content"
          >
            {heroContent[$audience].secondaryButton}
          </button>
        </div>
        <div class="mt-8">
          <p class="text-sm text-gray-500 mb-3">
            {heroContent[$audience].trustText}
          </p>
          <div class="flex flex-wrap gap-6 items-center">
            <img
              src="/images/landing_page/universities/harvard_logo.png"
              alt="Harvard Business School"
              class="max-w-[100px] max-h-[40px] opacity-70 object-contain"
            />
            <img
              src="/images/landing_page/universities/wharton_logo.png"
              alt="Wharton"
              class="max-w-[100px] max-h-[40px] opacity-70 object-contain"
            />
            <img
              src="/images/landing_page/universities/stanford_logo.png"
              alt="Stanford GSB"
              class="max-w-[100px] max-h-[40px] opacity-70 object-contain"
            />
          </div>
        </div>
      </div>
      <div class="md:w-1/2">
        <div class="relative">
          <div
            class="absolute -top-6 -left-6 w-24 h-24 bg-base-200 rounded-full z-0"
          ></div>
          <div
            class="absolute -bottom-6 -right-6 w-32 h-32 bg-base-200 rounded-full z-0"
          ></div>
          <img
            src={$audience === "students"
              ? "/images/landing_page/finance-interview-preparation-student.jpg"
              : "/images/landing_page/finance-interview-preparation-university.jpg"}
            alt={$audience === "students"
              ? "Finance interview preparation"
              : "University career services"}
            class="rounded-lg shadow-lg relative z-10 w-[600px] h-[500px] object-cover"
          />
        </div>
      </div>
    </div>
  </section>

  <!-- Finance Sectors Showcase (Students Only) -->
  {#if $audience === "students"}
    <section class="py-16 bg-base-200">
      <div class="container mx-auto px-6">
        <h2 class="text-3xl font-bold text-center mb-12 text-accent-content">
          Specialized Training for Top Finance Careers
        </h2>
        <div class="grid md:grid-cols-4 gap-8">
          {#each financeSectors as sector}
            <div
              class="bg-white rounded-lg border border-gray-200 p-6 transition-all duration-300 hover:shadow-md"
            >
              <div class="flex justify-center mb-4">
                <svelte:component
                  this={sector.icon}
                  class="w-12 h-12 text-primary"
                />
              </div>
              <h3
                class="text-xl font-bold text-center mb-3 text-accent-content"
              >
                {sector.title}
              </h3>
              <p class="text-accent-content text-center mb-6">
                {sector.description}
              </p>
              <div class="flex flex-wrap justify-center items-center gap-4">
                {#each sector.logos as logo}
                  <img
                    src={logo}
                    alt="{sector.title} company"
                    class="max-w-[140px] max-h-[80px] opacity-70 hover:opacity-100 transition-opacity object-contain"
                  />
                {/each}
              </div>
            </div>
          {/each}
        </div>
      </div>
    </section>
  {/if}

  <!-- Transform Your Finance Program (Universities Only) - SECTION 2 -->
  {#if $audience === "universities"}
    <section class="py-16 bg-base-200">
      <div class="container mx-auto px-6">
        <h2 class="text-3xl font-bold text-center mb-6 text-accent-content">
          Transform Your Finance Program
        </h2>
        <p
          class="text-lg text-center mb-12 max-w-3xl mx-auto text-accent-content"
        >
          Our comprehensive solution enhances your finance students' career
          outcomes and boosts your institution's reputation.
        </p>
        <div class="grid md:grid-cols-2 gap-8">
          <div
            class="bg-white rounded-lg border border-gray-200 p-6 transition-all duration-300 hover:shadow-md"
          >
            <div class="flex items-start mb-4">
              <Activity class="w-12 h-12 text-primary mr-4" />
              <div>
                <h3 class="text-xl font-bold mb-3 text-accent-content">
                  Enhance Your University's Competitive Edge
                </h3>
                <p class="text-accent-content mb-4">
                  Offer AI-driven prep that sets your institution apart from
                  other universities.
                </p>
                <p class="text-secondary font-medium">
                  <span class="font-bold">40%</span> increase in student engagement
                  with career services.
                </p>
              </div>
            </div>
          </div>

          <div
            class="bg-white rounded-lg border border-gray-200 p-6 transition-all duration-300 hover:shadow-md"
          >
            <div class="flex items-start mb-4">
              <FileText class="w-12 h-12 text-primary mr-4" />
              <div>
                <h3 class="text-xl font-bold mb-3 text-accent-content">
                  Elevate Your University's Placement Rates
                </h3>
                <p class="text-accent-content mb-4">
                  Help your students secure roles at top-tier firms, enhancing
                  your university's global ranking.
                </p>
                <p class="text-secondary font-medium">
                  <span class="font-bold">15%</span> improvement in finance program
                  rankings within one year.
                </p>
              </div>
            </div>
          </div>

          <div
            class="bg-white rounded-lg border border-gray-200 p-6 transition-all duration-300 hover:shadow-md"
          >
            <div class="flex items-start mb-4">
              <Brain class="w-12 h-12 text-primary mr-4" />
              <div>
                <h3 class="text-xl font-bold mb-3 text-accent-content">
                  Data-Driven Insights
                </h3>
                <p class="text-accent-content mb-4">
                  Faculty get real-time analytics to refine curricula.
                </p>
                <p class="text-secondary font-medium">
                  Cut iteration time by <span class="font-bold">50%</span> with performance
                  dashboards.
                </p>
              </div>
            </div>
          </div>

          <div
            class="bg-white rounded-lg border border-gray-200 p-6 transition-all duration-300 hover:shadow-md"
          >
            <div class="flex items-start mb-4">
              <Shield class="w-12 h-12 text-primary mr-4" />
              <div>
                <h3 class="text-xl font-bold mb-3 text-accent-content">
                  Seamless Integration & Support
                </h3>
                <p class="text-accent-content mb-4">
                  Dedicated account manager ensures quick rollout and minimal
                  overhead.
                </p>
                <p class="text-secondary font-medium">
                  Implementation usually done in <span class="font-bold"
                    >&lt;2 weeks</span
                  >.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  {/if}

  <!-- Student USPs / University Partnership Benefits - SECTION 3 -->
  <section class="py-20 bg-white">
    <div class="container mx-auto px-6">
      <h2 class="text-3xl font-bold text-center mb-4 text-accent-content">
        {$audience === "students"
          ? "Why Finance Candidates Choose Us"
          : "Why Partner with AceTheRound"}
      </h2>
      <p class="text-accent-content text-center mb-12 max-w-3xl mx-auto">
        {$audience === "students"
          ? "Our AI-powered platform is designed specifically for the unique challenges of finance interviews."
          : "Our platform is designed to complement your curriculum and enhance your students' career outcomes."}
      </p>

      {#if $audience === "students"}
        <div class="grid md:grid-cols-3 gap-8 mb-8">
          {#each studentUSPs.slice(0, 3) as usp}
            <div
              class="bg-white p-8 rounded-lg shadow-sm border border-gray-200 transition-all duration-300 hover:shadow-md"
            >
              <svelte:component
                this={usp.icon}
                class="w-10 h-10 text-primary mb-4"
              />
              <h3 class="text-xl font-bold mb-3 text-accent-content">
                {usp.title}
              </h3>
              <p class="text-accent-content mb-4">{usp.description}</p>
              <p class="text-secondary font-bold">{usp.stat}</p>
            </div>
          {/each}
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          {#each studentUSPs.slice(3) as usp}
            <div
              class="bg-white p-8 rounded-lg shadow-sm border border-gray-200 transition-all duration-300 hover:shadow-md"
            >
              <svelte:component
                this={usp.icon}
                class="w-10 h-10 text-primary mb-4"
              />
              <h3 class="text-xl font-bold mb-3 text-accent-content">
                {usp.title}
              </h3>
              <p class="text-accent-content mb-4">{usp.description}</p>
              <p class="text-secondary font-bold">{usp.stat}</p>
            </div>
          {/each}
        </div>
      {:else}
        <!-- University Partnership Benefits -->
        <div class="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div class="card bg-white shadow-xl">
            <div class="card-body">
              <h3 class="card-title text-accent-content">For Your Students</h3>
              <ul class="space-y-3">
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">AI-Powered Preparation:</span> Personalized
                    interview training that adapts to each student's strengths and
                    weaknesses
                  </span>
                </li>
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold"
                      >Industry-Specific Training:</span
                    > Specialized preparation for investment banking, private equity,
                    hedge funds, and more
                  </span>
                </li>
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">24/7 Access:</span> Practice anytime,
                    anywhere with unlimited access to our platform
                  </span>
                </li>
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">Confidence Building:</span> Students
                    report feeling 90% more prepared for actual interviews
                  </span>
                </li>
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">Community Support:</span> Access
                    to peer networks and group coaching sessions
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div class="card bg-white shadow-xl">
            <div class="card-body">
              <h3 class="card-title text-accent-content">
                For Your Institution
              </h3>
              <ul class="space-y-3">
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">Improved Metrics:</span> Higher placement
                    rates and starting salaries for your graduates
                  </span>
                </li>
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">Comprehensive Analytics:</span> Detailed
                    insights into student performance and preparation needs
                  </span>
                </li>
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">Employer Relationships:</span> Strengthen
                    connections with top finance firms through better-prepared candidates
                  </span>
                </li>
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">Competitive Edge:</span> Differentiate
                    your program with cutting-edge AI preparation tools
                  </span>
                </li>
                <li class="flex items-start">
                  <CheckCircle
                    class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span>
                    <span class="font-semibold">Scalable Solution:</span> Support
                    all your finance students without additional staff resources
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      {/if}
    </div>
  </section>

  <!-- University Pricing (Universities Only) - SECTION 4 -->
  {#if $audience === "universities"}
    <section id="pricing" class="bg-base-200 py-20 relative overflow-hidden">
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-primary) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-4 text-accent-content">
          Enterprise Partnership for Universities
        </h2>
        <p
          class="text-xl text-center mb-12 max-w-3xl mx-auto text-secondary font-medium"
        >
          Tailored solutions to meet your institution's specific needs and goals
        </p>

        <div class="max-w-4xl mx-auto">
          <div class="card bg-white border-2 border-primary shadow-xl">
            <div class="card-body">
              <div class="text-center pb-4">
                <h3 class="text-2xl font-bold text-accent-content">
                  Comprehensive University Partnership
                </h3>
                <p class="text-lg text-gray-600">
                  Custom solutions for finance departments and career services
                </p>
              </div>

              <div class="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 class="font-semibold text-lg mb-4 text-secondary">
                    Platform Features
                  </h4>
                  <ul class="space-y-3">
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>Bulk licensing for all finance students</span>
                    </li>
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>AI-powered interview simulation</span>
                    </li>
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>Industry-specific training modules</span>
                    </li>
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>Unlimited practice sessions</span>
                    </li>
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>Personalized feedback and coaching</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 class="font-semibold text-lg mb-4 text-secondary">
                    Institution Benefits
                  </h4>
                  <ul class="space-y-3">
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>Comprehensive analytics dashboard</span>
                    </li>
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>Dedicated account manager</span>
                    </li>
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>Faculty training and resources</span>
                    </li>
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>On-campus workshops and events</span>
                    </li>
                    <li class="flex items-start">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span>Quarterly performance reviews</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="mt-8 text-center">
                <p class="text-lg font-medium text-secondary mb-2">
                  Custom pricing based on your institution's size and needs
                </p>
                <p class="text-sm text-gray-600 mb-6">
                  Volume discounts available for larger departments
                </p>
              </div>

              <div class="card-actions justify-center">
                <a
                  href="/contact_us"
                  class="btn btn-lg bg-accent text-accent-content hover:bg-yellow-500 font-medium px-8"
                >
                  Request a Custom Quote
                </a>
              </div>
            </div>
          </div>
        </div>

        <p class="text-center mt-8 text-sm text-gray-600">
          All partnerships include dedicated support and regular performance
          reviews.
        </p>
      </div>
    </section>

    <!-- University ROI Calculator - SECTION 5 -->
    <section class="py-20 relative overflow-hidden bg-white">
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-neutral) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-4 text-accent-content">
          Calculate Your University's ROI
        </h2>
        <p
          class="text-lg text-center mb-12 max-w-3xl mx-auto text-secondary font-medium"
        >
          See the tangible impact AceTheRound can have on your students' career
          outcomes and your institution's success
        </p>
        <UniversityROICalculator />
      </div>
    </section>

    <!-- University Testimonials - SECTION 6 -->
    <section class="py-20 relative overflow-hidden bg-base-200">
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-neutral) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-12 text-accent-content">
          What Partner Universities Say
        </h2>
        <div class="grid md:grid-cols-2 gap-8">
          {#each universityTestimonials as testimonial}
            <div class="card bg-white shadow-xl">
              <div class="card-body">
                <h3 class="card-title text-accent-content">
                  {testimonial.name}
                </h3>
                <p class="text-sm text-gray-600">{testimonial.position}</p>
                <p class="mt-4 text-accent-content">{testimonial.content}</p>
              </div>
            </div>
          {/each}
        </div>
      </div>
    </section>
  {/if}

  <!-- How It Works / Your Roadmap (Students Only) -->
  {#if $audience === "students"}
    <section
      id="how-it-works"
      class="bg-base-200 py-20 relative overflow-hidden"
    >
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-primary) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-12 text-accent-content">
          Your Roadmap to Landing a Finance Offer
        </h2>
        <div class="relative">
          <div
            class="absolute top-0 bottom-0 left-1/2 w-1 bg-primary transform -translate-x-1/2"
          ></div>
          {#each howItWorksSteps as step, index}
            <div
              class="flex items-center mb-8 {index % 2 === 0
                ? 'flex-row'
                : 'flex-row-reverse'}"
            >
              <div class="w-1/2 {index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}">
                <h3 class="text-xl font-semibold mb-2 text-accent-content">
                  {step.title}
                </h3>
                <p class="text-accent-content">{step.description}</p>
              </div>
              <div
                class="w-10 h-10 bg-primary rounded-full flex items-center justify-center z-10"
              >
                <svelte:component this={step.icon} class="w-6 h-6 text-white" />
              </div>
              <div class="w-1/2 {index % 2 === 0 ? 'pl-8' : 'pr-8'}"></div>
            </div>
          {/each}
        </div>
      </div>
    </section>

    <!-- Student Testimonials -->
    <section class="py-20 relative overflow-hidden">
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-neutral) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-12 text-accent-content">
          Success Stories from Students Like You
        </h2>
        <div class="grid md:grid-cols-2 gap-8">
          {#each studentTestimonials as testimonial}
            <div class="card bg-white shadow-xl">
              <div class="card-body">
                <h3 class="card-title text-accent-content">
                  {testimonial.name}
                </h3>
                <p class="text-sm text-gray-600">{testimonial.position}</p>
                <p class="mt-4 text-accent-content">{testimonial.content}</p>
              </div>
            </div>
          {/each}
        </div>
      </div>
    </section>

    <!-- Student Pricing -->
    <section id="pricing" class="bg-base-200 py-20 relative overflow-hidden">
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-primary) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-4 text-accent-content">
          Affordable Plans for Every Aspiring Finance Pro
        </h2>
        <p
          class="text-xl text-center mb-12 max-w-3xl mx-auto text-secondary font-medium"
        >
          Invest in Your Future: The Right Plan Can Transform Your Career
          Trajectory
        </p>
        <div class="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {#each pricingPlans as plan}
            <div
              class="card bg-white shadow-xl {plan.highlight
                ? 'border-2 border-primary'
                : ''}"
            >
              <div class="card-body">
                <h3 class="card-title text-accent-content">{plan.name}</h3>
                <p class="text-gray-600">{plan.description}</p>
                <p class="text-3xl font-bold mb-4 text-accent-content">
                  {plan.price}<span class="text-lg font-normal"
                    >{plan.period}</span
                  >
                </p>
                <ul class="space-y-2">
                  {#each plan.features as feature}
                    <li class="flex items-center text-accent-content">
                      <CheckCircle
                        class="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                      />
                      <span>{feature}</span>
                    </li>
                  {/each}
                </ul>
                <p class="mt-4 font-semibold text-accent-content">
                  {plan.dailyCost}
                </p>
                <div class="card-actions justify-end mt-4">
                  <a
                    href="/pricing"
                    class="btn w-full bg-accent text-accent-content hover:bg-yellow-500"
                    >{plan.buttonText}</a
                  >
                </div>
              </div>
            </div>
          {/each}
        </div>
        <div
          class="max-w-4xl mx-auto mt-8 mb-6 bg-blue-50 border border-primary rounded-lg p-6 flex items-center justify-center shadow-sm"
        >
          <div
            class="flex flex-col sm:flex-row items-center text-center sm:text-left"
          >
            <div class="mr-0 sm:mr-6 mb-4 sm:mb-0">
              <Shield class="h-12 w-12 text-secondary" />
            </div>
            <div>
              <h3 class="text-xl font-bold text-secondary mb-1">
                7-Day Money Back Guarantee
              </h3>
              <p class="text-accent-content">
                Try AceTheRound risk-free. If you're not satisfied for any
                reason, we'll refund your payment. No questions asked.
              </p>
            </div>
          </div>
        </div>
        <p class="text-center mt-8 text-sm text-gray-600">
          Cancel anytime. No hidden fees.
        </p>
      </div>
    </section>

    <!-- Student ROI Calculator Section -->
    <section class="py-20 relative overflow-hidden">
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-neutral) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-12 text-accent-content">
          Calculate Your Return on Investment (ROI)
        </h2>
        <StudentROICalculator />
      </div>
    </section>

    <!-- Comparison Chart (Students Only) -->
    <section class="py-20 relative overflow-hidden">
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-neutral) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-12 text-accent-content">
          See How AceTheRound Stacks Up Against Other Prep Methods
        </h2>
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-primary">
                <th class="border p-3 text-left text-white">Features</th>
                {#each comparisonColumns as column}
                  <th class="border p-3 text-center text-white">{column}</th>
                {/each}
              </tr>
            </thead>
            <tbody>
              {#each comparisonFeatures as feature, featureIndex}
                <tr class={featureIndex % 2 === 0 ? "bg-gray-50" : ""}>
                  <td class="border p-3 font-semibold text-accent-content"
                    >{feature}</td
                  >
                  {#each comparisonData as columnData}
                    <td class="border p-3 text-center">
                      {#if columnData[featureIndex] === true}
                        <CheckCircle
                          class="inline-block w-6 h-6 text-green-500"
                        />
                      {:else if columnData[featureIndex] === "partial"}
                        <MinusCircle
                          class="inline-block w-6 h-6 text-yellow-500"
                        />
                      {:else}
                        <XCircle class="inline-block w-6 h-6 text-red-500" />
                      {/if}
                    </td>
                  {/each}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- FAQ Section (Students Only) -->
    <section id="faq" class="bg-gray-100 py-20 relative overflow-hidden">
      <div class="absolute inset-0 opacity-5">
        <div
          class="absolute inset-0"
          style="background-image: radial-gradient(circle, var(--color-neutral) 1px, transparent 1px); background-size: 20px 20px;"
        ></div>
      </div>
      <div class="container mx-auto px-6 relative z-10">
        <h2 class="text-3xl font-bold text-center mb-12 text-accent-content">
          Frequently Asked Questions
        </h2>
        <div class="max-w-3xl mx-auto space-y-4">
          {#each faqs as faq, index}
            <div class="collapse collapse-plus bg-white border border-gray-200">
              <input type="radio" name="faq-accordion" checked={index === 0} />
              <div
                class="collapse-title text-lg font-medium text-accent-content"
              >
                {faq.question}
              </div>
              <div class="collapse-content">
                <p class="text-gray-800">{faq.answer}</p>
              </div>
            </div>
          {/each}
        </div>
      </div>
    </section>
  {/if}

  <!-- Final CTA (exact prototype copy) -->
  <section class="bg-primary py-20">
    <div class="container mx-auto px-6 text-center">
      <h2 class="text-4xl font-bold mb-6 text-white">
        {$audience === "students"
          ? "Ready to Ace Your Finance Interviews?"
          : "Ready to Transform Your Students' Career Outcomes?"}
      </h2>
      <p class="text-xl mb-8 text-white max-w-3xl mx-auto">
        {$audience === "students"
          ? "Don't leave your future to chance. Start your journey to interview mastery today."
          : "Partner with us to give your students the competitive edge they need in finance interviews."}
      </p>
      <div class="flex flex-col sm:flex-row justify-center gap-4">
        <a
          href={$audience === "students" ? "/pricing" : "/contact_us"}
          class="btn btn-lg bg-accent text-accent-content hover:bg-yellow-500 font-medium px-8"
        >
          {$audience === "students"
            ? "Start Free Trial"
            : "Partnership Inquiry"}
        </a>
        <button
          class="btn btn-lg btn-outline border-white text-white hover:bg-white hover:text-primary font-medium"
        >
          Watch Demo
        </button>
      </div>
    </div>
  </section>
</div>
